/**
 * SharedStyles.js
 * Centralized responsive styled components used across master pages and transaction pages.
 * All responsive breakpoints are applied here so individual pages stay DRY.
 */
import React, { createContext, useContext } from 'react';
import styled from 'styled-components';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import { media } from '../theme/breakpoints';

// ═══════════════════════════════════════════════════
//  TABLE SORTING (used with useTableSort hook)
// ═══════════════════════════════════════════════════
export const SortContext = createContext({ sortField: null, sortDir: 'asc', onSort: () => {} });

const SortIndicator = ({ active, dir }) => (
  <span style={{
    display: 'inline-flex', flexDirection: 'column', lineHeight: 0.7,
    marginLeft: 4, fontSize: '9px', opacity: active ? 1 : 0.35, userSelect: 'none'
  }}>
    <span style={{ color: active && dir === 'asc' ? colors.primary.main : 'inherit' }}>▲</span>
    <span style={{ color: active && dir === 'desc' ? colors.primary.main : 'inherit' }}>▼</span>
  </span>
);

export const SortableTh = ({ field, children, style, ...rest }) => {
  const { sortField, sortDir, onSort } = useContext(SortContext);
  const active = sortField === field;
  const align = (style && style.textAlign) || 'left';
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';
  return (
    <Th
      {...rest}
      onClick={() => onSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      title="Click to sort"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: justify, width: '100%' }}>
        {children}
        <SortIndicator active={active} dir={sortDir} />
      </span>
    </Th>
  );
};

// ═══════════════════════════════════════════════════
//  MASTER PAGE COMPONENTS (Customers, Vendors, etc.)
// ═══════════════════════════════════════════════════

export const MasterHeaderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;

  ${media.sm} {
    justify-content: stretch;
    button { flex: 1; justify-content: center; }
  }
`;

export const MasterAddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }

  ${media.sm} {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
  }
`;

export const ContentCard = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
  overflow: hidden;
`;

// ═══════════════════════════════════════════════════
//  TOTALS / STATS CARDS (used at top of master pages)
// ═══════════════════════════════════════════════════

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
  margin-bottom: ${spacing.md};
`;

export const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: white;
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  box-shadow: ${shadows.sm};
`;

export const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
  }
`;

export const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const StatValue = styled.span`
  font-size: ${typography.fontSize.h4};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

export const MasterSearchBar = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};

  ${media.sm} {
    padding: 12px;
  }
`;

export const MasterSearchInput = styled.div`
  position: relative;
  max-width: 400px;

  ${media.sm} {
    max-width: 100%;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

export const MasterInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

export const MasterTableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const MasterTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const MasterThead = styled.thead`
  background: ${colors.neutral[50]};

  ${media.sm} {
    display: none;
  }
`;

export const MasterTh = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const MasterTbody = styled.tbody``;

export const MasterTr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  ${media.sm} {
    display: block;
    padding: 12px;
    margin-bottom: 8px;
    border: 1px solid ${colors.neutral[200]};
    border-radius: ${borderRadius.md};
    background: white;
    &:hover { background: white; }
  }
`;

export const MasterTd = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${colors.text.primary};

  ${media.sm} {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    border: none;

    &::before {
      content: attr(data-label);
      font-size: 12px;
      font-weight: 600;
      color: ${colors.text.secondary};
      margin-right: 12px;
      flex-shrink: 0;
    }

    &:last-child {
      justify-content: flex-end;
      padding-top: 8px;
      margin-top: 4px;
      border-top: 1px solid ${colors.neutral[100]};
    }
  }
`;

export const MasterBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.active ? colors.success.main + '20' : colors.neutral[200]};
  color: ${props => props.active ? colors.success.main : colors.text.secondary};
`;

export const MasterActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const MasterIconButton = styled.button`
  padding: 6px;
  background: ${props => props.danger ? colors.error.main : colors.primary.main};
  color: white;
  border: none;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.danger ? colors.error.dark : colors.primary.dark};
    transform: scale(1.05);
  }

  svg { font-size: 14px; }

  ${media.sm} {
    padding: 8px;
    svg { font-size: 16px; }
  }
`;

// ── Master Modal ──
export const MasterModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

export const MasterModalContent = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  padding: 24px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;

  ${media.sm} {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
    padding: 16px;
  }
`;

export const MasterModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  ${media.sm} {
    margin-bottom: 16px;
    position: sticky;
    top: 0;
    background: white;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutral[200]};
    z-index: 1;
  }
`;

export const MasterModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;

  ${media.sm} {
    font-size: 18px;
  }
`;

export const MasterCloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover { color: ${colors.text.primary}; }

  ${media.sm} {
    padding: 8px;
  }
`;

export const MasterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.sm} {
    gap: 12px;
  }
`;

export const MasterFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const MasterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

export const MasterFormInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  ${media.sm} {
    padding: 12px;
    font-size: 16px;
  }
`;

export const MasterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  ${media.sm} {
    padding: 12px;
    font-size: 16px;
  }
`;

export const MasterTextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  ${media.sm} {
    font-size: 16px;
  }
`;

export const MasterFormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  ${media.sm} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const MasterModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;

  ${media.sm} {
    position: sticky;
    bottom: 0;
    background: white;
    padding-top: 12px;
    border-top: 1px solid ${colors.neutral[200]};
    margin-top: 16px;

    button {
      flex: 1;
      justify-content: center;
    }
  }
`;

export const MasterButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${media.sm} {
    padding: 12px 16px;
  }
`;

export const MasterEmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};

  ${media.sm} {
    padding: 40px 16px;
  }
`;

export const MasterEmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

export const MasterEmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;

  ${media.sm} {
    font-size: 14px;
  }
`;


// ═══════════════════════════════════════════════════
//  TRANSACTION PAGE COMPONENTS (Invoices, POs, etc.)
// ═══════════════════════════════════════════════════

export const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;

  ${media.sm} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 400px;

  svg { color: ${colors.text.secondary}; flex-shrink: 0; }
  input {
    border: none;
    outline: none;
    background: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    min-width: 0;
  }

  ${media.sm} {
    max-width: 100%;
    input { font-size: 16px; }
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  ${media.sm} {
    width: 100%;
    > * { flex: 1; }
  }
`;

export const StatusFilterSelect = styled.select`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  background: ${colors.background.default};
  cursor: pointer;
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }

  ${media.sm} {
    padding: 10px ${spacing.md};
    font-size: 16px;
  }
`;

export const TranAddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  &:hover { background: ${colors.primary.dark}; }

  ${media.sm} {
    justify-content: center;
    padding: 10px ${spacing.md};
  }
`;

export const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xxl};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.md};
`;

export const TranEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xxl};
  gap: ${spacing.md};
  color: ${colors.text.secondary};
  h3 { color: ${colors.text.primary}; margin: 0; }
  p { margin: 0; }
  svg { opacity: 0.3; }

  ${media.sm} {
    padding: ${spacing.xl} ${spacing.md};
  }
`;

export const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  background: ${colors.background.default};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Th = styled.th`
  text-align: left;
  padding: 12px ${spacing.md};
  background: ${colors.neutral[100]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.border.light};
  white-space: nowrap;
`;

export const Tr = styled.tr`
  &:hover { background: ${colors.neutral[50]}; }
  &:not(:last-child) td { border-bottom: 1px solid ${colors.neutral[100]}; }
`;

export const Td = styled.td`
  padding: 10px ${spacing.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  white-space: nowrap;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: white;
  background: ${props => props.$color || colors.neutral[500]};
`;

export const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
`;

export const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  transition: all 0.2s;
  &:hover {
    background: ${props => props.$danger ? colors.error.main + '10' : colors.neutral[100]};
    border-color: ${props => props.$danger ? colors.error.main : colors.border.main};
  }
  svg { font-size: 14px; }

  ${media.sm} {
    width: 34px;
    height: 34px;
    svg { font-size: 16px; }
  }
`;

export const SummaryBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md};
  background: ${colors.neutral[50]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};

  ${media.sm} {
    flex-direction: column;
    gap: ${spacing.sm};
    text-align: center;
  }
`;

// ── Form Layout (Transaction) ──
export const FormHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;

  ${media.sm} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: transparent;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
  transition: all 0.2s;
  &:hover { border-color: ${colors.primary.main}; color: ${colors.primary.main}; }
`;

export const FormTitle = styled.h3`
  flex: 1;
  margin: 0;
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  display: flex;
  align-items: center;

  ${media.sm} {
    font-size: ${typography.fontSize.md};
  }
`;

export const FormActions = styled.div`
  display: flex;
  gap: ${spacing.sm};

  ${media.sm} {
    flex-wrap: wrap;
    > * { flex: 1; min-width: 0; justify-content: center; }
  }
`;

export const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.lg};
  background: ${props => props.$secondary ? 'transparent' : props.$approve ? colors.success.main : colors.primary.main};
  color: ${props => props.$secondary ? colors.text.secondary : colors.text.white};
  border: 1px solid ${props => props.$secondary ? colors.border.light : 'transparent'};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  ${media.sm} {
    padding: 10px ${spacing.md};
  }
`;

export const FormCard = styled.div`
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  padding: ${spacing.lg};

  ${media.sm} {
    padding: ${spacing.md};
    border-radius: ${borderRadius.sm};
  }
`;

export const SectionTitle = styled.h4`
  margin: 0 0 ${spacing.md} 0;
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${colors.border.light};
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing.md};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.sm} {
    grid-template-columns: 1fr;
    gap: ${spacing.sm};
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${props => props.$span2 && 'grid-column: span 2;'}

  ${media.sm} {
    ${props => props.$span2 && 'grid-column: span 1;'}
  }
`;

export const Label = styled.label`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.secondary};
`;

export const Input = styled.input`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[100]}; color: ${colors.text.secondary}; }

  ${media.sm} {
    padding: 10px ${spacing.md};
    font-size: 16px;
  }
`;

export const Select = styled.select`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  background: ${colors.background.default};
  outline: none;
  cursor: pointer;
  &:focus { border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[100]}; }

  ${media.sm} {
    padding: 10px ${spacing.md};
    font-size: 16px;
  }
`;

export const TextArea = styled.textarea`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
  outline: none;
  resize: vertical;
  font-family: inherit;
  &:focus { border-color: ${colors.primary.main}; }

  ${media.sm} {
    font-size: 16px;
  }
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing.md};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.sm} {
    grid-template-columns: 1fr;
    gap: ${spacing.sm};
  }
`;

export const InfoItem = styled.div`
  label {
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
    display: block;
    margin-bottom: 2px;
  }
  span {
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
  }
`;

export const NotesText = styled.p`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
`;

// ── Line Items ──
export const LineItemsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.md};
  padding-bottom: ${spacing.sm};
  border-bottom: 1px solid ${colors.border.light};
`;

export const AddLineButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: 6px ${spacing.md};
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}40;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: ${colors.primary.main}20; }

  ${media.sm} {
    padding: 8px ${spacing.md};
  }
`;

export const LineTableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
`;

export const LineTable = styled.table`
  table-layout: fixed;
  border-collapse: collapse;
`;

export const LineTh = styled.th`
  text-align: left;
  padding: 8px;
  background: ${colors.neutral[100]};
  font-size: 11px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 1px solid ${colors.border.light};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const LineTd = styled.td`
  padding: 4px 6px;
  font-size: ${typography.fontSize.sm};
  border-bottom: 1px solid ${colors.neutral[100]};
  white-space: nowrap;
  overflow: hidden;
`;

export const LineInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${colors.border.light};
  border-radius: 3px;
  font-size: ${typography.fontSize.sm};
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[50]}; }
`;

export const LineSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${colors.border.light};
  border-radius: 3px;
  font-size: ${typography.fontSize.sm};
  outline: none;
  background: white;
  &:focus { border-color: ${colors.primary.main}; }
  &:disabled { background: ${colors.neutral[50]}; }
`;

export const RemoveLineBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  color: ${colors.error.main};
  cursor: pointer;
  border-radius: 3px;
  &:hover { background: ${colors.error.main}10; }
  svg { font-size: 14px; }
`;

// ── Totals ──
export const TotalsSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing.md};

  ${media.sm} {
    justify-content: stretch;
  }
`;

export const TotalsTable = styled.table`
  width: 320px;
  border-collapse: collapse;
  td {
    padding: 6px ${spacing.md};
    font-size: ${typography.fontSize.sm};
  }
  td:first-child { color: ${colors.text.secondary}; }
  td:last-child { text-align: right; font-weight: ${typography.fontWeight.medium}; }

  ${media.sm} {
    width: 100%;
  }
`;

export const TotalRow = styled.tr`
  td {
    border-top: 2px solid ${colors.primary.main};
    font-weight: ${typography.fontWeight.bold} !important;
    font-size: ${typography.fontSize.md} !important;
    color: ${colors.primary.main} !important;
    padding-top: ${spacing.sm} !important;
  }
`;

export const BalanceRow = styled.tr`
  td {
    border-top: 1px solid ${colors.border.light};
    font-weight: ${typography.fontWeight.semibold} !important;
    color: ${colors.error.main} !important;
  }
`;

export const DiscountInput = styled.input`
  width: 100px;
  padding: 4px 8px;
  border: 1px solid ${colors.border.light};
  border-radius: 3px;
  text-align: right;
  font-size: ${typography.fontSize.sm};
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }

  ${media.sm} {
    width: 100%;
  }
`;

export const PaymentSection = styled.div`
  margin-top: ${spacing.lg};
  padding-top: ${spacing.md};
  border-top: 1px solid ${colors.border.light};
`;

export const LinkedBadgeContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: ${colors.info.main}15;
  color: ${colors.info.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  margin-right: 4px;
`;

export const LinkedDocsSection = styled.div`
  display: flex;
  gap: ${spacing.sm};
  flex-wrap: wrap;
  margin-top: ${spacing.md};
  padding-top: ${spacing.md};
  border-top: 1px dashed ${colors.border.light};
`;
